import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCourse } from '../contexts/CourseContext';
import CourseFilterBar from '../components/CourseFilterBar';
import CourseCard from '../components/CourseCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import './DashboardView.css';

const DashboardView: React.FC = () => {
  const navigate = useNavigate();
  const {
    enrolledCourses,
    isLoading,
    error,
    fetchEnrolledCourses,
    filterCourses,
    clearError,
  } = useCourse();

  const [activeFilter, setActiveFilter] = useState<'All' | 'Live' | 'Self-Paced'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCourses, setFilteredCourses] = useState(enrolledCourses);

  // Fetch enrolled courses on mount
  useEffect(() => {
    fetchEnrolledCourses();
  }, [fetchEnrolledCourses]);

  // Apply filters and search
  useEffect(() => {
    let courses = filterCourses(activeFilter);

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      courses = courses.filter(
        (course) =>
          course.title.toLowerCase().includes(query) ||
          course.instructor.toLowerCase().includes(query) ||
          course.description.toLowerCase().includes(query)
      );
    }

    setFilteredCourses(courses);
  }, [enrolledCourses, activeFilter, searchQuery, filterCourses]);

  const handleFilterChange = (filter: 'All' | 'Live' | 'Self-Paced') => {
    setActiveFilter(filter);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleCourseNavigate = (courseId: string) => {
    navigate(`/dashboard/courses/${courseId}`);
  };

  if (isLoading && enrolledCourses.length === 0) {
    return (
      <div className="dashboard-view">
        <LoadingSpinner fullScreen />
      </div>
    );
  }

  return (
    <div className="dashboard-view">
      <div className="dashboard-header">
        <h1 className="dashboard-title">My Courses</h1>
        <p className="dashboard-subtitle">
          Track your progress and continue learning
        </p>
      </div>

      {error && (
        <ErrorMessage
          message={error}
          type="error"
          onDismiss={clearError}
        />
      )}

      <CourseFilterBar
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
      />

      {filteredCourses.length === 0 ? (
        <div className="empty-state">
          <svg
            className="empty-state-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          <h3 className="empty-state-title">
            {searchQuery ? 'No courses found' : 'No courses enrolled'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? 'Try adjusting your search or filter criteria'
              : 'Start your learning journey by enrolling in a course'}
          </p>
        </div>
      ) : (
        <div className="course-grid">
          {filteredCourses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              onNavigate={handleCourseNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardView;
